import React, { useState, useEffect } from 'react';

const baseURL = process.env.REACT_APP_API_BASE_URL;

const CategorySelect = ({ 
  onCategorySelect, 
  initialCategoryId = null,
  apiUrl = `${baseURL}/api/categories/`,
  className = '',
  showActiveOnly = true,
  placeholder = 'Select a category',
  name = 'categoryId'
}) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        // Filter categories based on showActiveOnly prop
        const filteredCategories = showActiveOnly 
          ? data.filter(category => category.status === 1)
          : data;
          
        setCategories(filteredCategories);
        
        // Set initial selection if provided
        if (initialCategoryId) {
          const initialCategory = filteredCategories.find(
            category => category.categoryId === initialCategoryId
          );
          if (initialCategory) {
            setSelectedCategory(initialCategory);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [apiUrl, initialCategoryId, showActiveOnly]);

  const handleSelect = (category) => {
    setSelectedCategory(category);
    setIsOpen(false);
    // Call the parent's callback function with category and ID
    if (onCategorySelect) {
      onCategorySelect(category.categoryId);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden input to store the categoryId value for form submission */}
      <input 
        type="hidden" 
        name={name} 
        value={selectedCategory?.categoryId || ''} 
      />
      
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full p-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        disabled={isLoading}
      >
        <span className="block truncate">
          {selectedCategory ? selectedCategory.title : placeholder}
        </span>
        <div className="flex items-center">
          {/* {selectedCategory && (
            <span className="mr-2 text-xs text-gray-500">
              ID: {selectedCategory.categoryId}
            </span>
          )} */}
          <svg 
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="w-4 h-4 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && !isLoading && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-scroll">
          {categories.length > 0 ? (
            categories.map((category) => (
              <li
                key={category.categoryId}
                onClick={() => handleSelect(category)}
                className="px-3 py-2 cursor-pointer hover:bg-blue-50 focus:bg-blue-100"
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{category.title}</span>
                  <div className="flex items-center">
                    {/* <span className="text-xs text-gray-500 mr-2">
                      ID: {category.categoryId}
                    </span> */}
                    {selectedCategory?.categoryId === category.categoryId && (
                      <svg className="w-4 h-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="px-3 py-2 text-gray-500 text-center">No categories available</li>
          )}
          {error && (
            <li className="px-3 py-2 text-red-500 text-center">Error: {error}</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CategorySelect;